import { isEuCountry, normalizeCountry } from "@talentkontor/domain";
import { t, tr, type Lang } from "../lib/sprache";

/**
 * Anwaltsvorgaben Punkt 11 und 23 (0181): CreatorOffice entscheidet die steuerliche Behandlung nicht selbst. Es setzt das Steuerprofil
 * (Sitzland, Steuerstatus, USt-IdNr.) des Kunden um, schlägt den Steuerfall vor und verlangt in komplexen Fällen eine ausdrückliche
 * Bestätigung (taxTreatmentNeedsConfirmation, Datenbank: invoices.tax_treatment_confirmed_at). Die Texte stehen an einer Stelle,
 * damit Einstellungen, Creator-Profil und Rechnungserstellung gleich formulieren.
 */

/** Oberflächensprache der Agentur (t) oder ausdrücklich gewählte Sprache (Creator-Portal, tr) */
const tx = (lang: Lang | undefined, de: string, en: string) => (lang ? tr(lang, de, en) : t(de, en));

/** Satz an Steuerprofil und Steuerstatus (Einstellungen · Firma, Creator-Profil) */
export const steuerVerantwortung = (lang?: Lang) => tx(lang,
  "Du bist für die Richtigkeit deiner steuerlichen Angaben und die Auswahl der zutreffenden steuerlichen Behandlung verantwortlich. CreatorOffice stellt keine Steuerberatung dar.",
  "You are responsible for the accuracy of your tax details and for choosing the applicable tax treatment. CreatorOffice does not provide tax advice.");

/**
 * Verständlichkeit C2-04/E-02: der Steuerfall in Alltagssprache, damit das Häkchen eine Aussage bestätigt und keine Bitte.
 * Schlüssel wie taxModeFromDb (REVERSE_CHARGE_EU …); unbekannte Werte: null.
 */
export const steuerfallKlartext = (mode: string | null | undefined, lang?: Lang): string | null => {
  switch (mode) {
    case "REVERSE_CHARGE_EU": return tx(lang,
      "Auf dieser Rechnung steht keine Umsatzsteuer, weil der Empfänger ein Unternehmen in einem anderen EU-Land ist und die Steuer dort selbst abführt (Reverse Charge).",
      "This invoice shows no VAT because the recipient is a business in another EU country and pays the tax there itself (reverse charge).");
    case "REVERSE_CHARGE_DOMESTIC": return tx(lang,
      "Auf dieser Rechnung steht keine Umsatzsteuer, weil der Empfänger die Steuer selbst schuldet (Steuerschuld beim Empfänger).",
      "This invoice shows no VAT because the recipient owes the tax itself (reverse charge).");
    case "NOT_TAXABLE_ABROAD": return tx(lang,
      "Auf dieser Rechnung steht keine Umsatzsteuer, weil die Leistung im Ausland erbracht gilt (nicht steuerbar).",
      "This invoice shows no VAT because the service counts as supplied abroad (not taxable).");
    case "SMALL_BUSINESS": return tx(lang,
      "Auf dieser Rechnung steht keine Umsatzsteuer, weil der Aussteller Kleinunternehmer ist.",
      "This invoice shows no VAT because the issuer is a small business.");
    case "DOMESTIC": return tx(lang, "Auf dieser Rechnung steht Umsatzsteuer.", "This invoice shows VAT.");
    case "MANUAL": return tx(lang, "Der Steuerfall dieser Rechnung ist von Hand gewählt.", "The tax treatment of this invoice was chosen manually.");
    default: return null;
  }
};

/** Bestätigung vor dem Festschreiben bzw. Einreichen; mit Steuerfall als Aussage in Alltagssprache (C2-04) */
export const steuerBestaetigung = (lang?: Lang, mode?: string | null) => {
  const klar = steuerfallKlartext(mode, lang);
  return klar
    ? tx(lang, `Geprüft: ${klar} CreatorOffice stellt keine Steuerberatung dar.`, `Checked: ${klar} CreatorOffice does not provide tax advice.`)
    : tx(lang,
      "Bitte bestätige die steuerliche Behandlung dieser Rechnung. CreatorOffice stellt keine Steuerberatung dar.",
      "Please confirm the tax treatment of this invoice. CreatorOffice does not provide tax advice.");
};

/** Kurzer Hinweis am Steuerfall der Rechnung: Vorschlag, nicht Entscheidung */
export const steuerVorschlagHinweis = (lang?: Lang) => tx(lang,
  "Der Steuerfall ist ein Vorschlag aus dem Steuerprofil und den Angaben zum Empfänger. Ob er zutrifft, prüfst du selbst.",
  "The tax treatment is a suggestion based on the tax profile and the recipient's details. You check yourself whether it applies.");

/**
 * Steuerprüfung 23.10.2026, L3: Die Kleinunternehmerregelung wirkt im Produkt nur im Sitzland. Die EU-Kleinunternehmerregelung
 * (Befreiung in anderen Mitgliedstaaten, Nummer mit Zusatz „EX“, § 19a UStG, Art. 284 ff. MwStSystRL) ist nicht abgebildet; kein
 * Schalter, nur dieser Hinweis im Kleinunternehmer-Dialog. Nur für Sitz in der EU (Schweiz und Liechtenstein: null).
 */
export const kleinunternehmerNurSitzland = (country: string | null | undefined, lang?: Lang): string | null => {
  const c = normalizeCountry(country ?? null);
  if (!c || !isEuCountry(c)) return null;
  return tx(lang,
    "Die Kleinunternehmerregelung gilt hier nur für Umsätze im Sitzland. Die EU-Kleinunternehmerregelung (Steuerbefreiung in anderen EU-Ländern mit einer Nummer mit Zusatz „EX“, § 19a UStG bzw. Art. 284 ff. MwStSystRL) unterstützt CreatorOffice nicht. Leistungen an Privatpersonen in anderen EU-Ländern sind damit nicht befreit; solche Rechnungen gehen in die manuelle Prüfung.",
    "The small business scheme applies here only to sales in the country of residence. CreatorOffice does not support the EU small business scheme (exemption in other EU countries with a number with the suffix “EX”, § 19a German VAT Act or Art. 284 et seq. VAT Directive). Services to private individuals in other EU countries are therefore not exempt; such invoices go to manual review.");
};

/** Hinweis an der Auswahl Reverse Charge bzw. Kleinunternehmer */
export const steuerAuswahlHinweis = (lang?: Lang) => tx(lang,
  "Reverse Charge, „nicht steuerbar“ und der Kleinunternehmerstatus hängen von Voraussetzungen ab, die CreatorOffice nicht prüfen kann (zum Beispiel Unternehmereigenschaft und gültige USt-IdNr. des Empfängers). Im Zweifel mit der Steuerberatung klären.",
  "Reverse charge, “not taxable” and small business status depend on requirements that CreatorOffice cannot check (for example the recipient's business status and valid VAT ID). If in doubt, check with your tax advisor.");
